import { components, paths } from "@/api-schema";
import { PathsV1CompanyUuidGetParametersQueryExtend } from "@/api-schema-enums";
import { useCompanyByUuidQuery } from "@/common/api/company/use-company-by-uuid.query";
import { CenteredLayout, CenteredLayoutVariant } from "@/components/layouts/centered-layout";
import { UuidExistenceWrapper } from "@/components/uuid-existence-wrapper/uuid-existence-wrapper";
import { useTranslation } from "react-i18next";
import { useParams } from "wouter";
import { CompanyUpdateFormFields } from "./_components/update-form-fields";

interface UpdateViewParams {
  uuid: string;
}

type Company = components["schemas"]["Company"];

export function UpdateView() {
  const { t } = useTranslation(["masterdata", "common"]);
  const params = useParams<UpdateViewParams>();
  const uuid = params.uuid;
  const companyQuery = useCompanyByUuidQuery(uuid, {
    parameters: {
      extend: [
        PathsV1CompanyUuidGetParametersQueryExtend.CompanyIdentifiers,
        PathsV1CompanyUuidGetParametersQueryExtend.ManagingDirectorOccupations,
        PathsV1CompanyUuidGetParametersQueryExtend.CompanyGroup,
      ],
    },
  });

  return (
    <UuidExistenceWrapper<
      paths["/v1/company/{uuid}"]["get"]["parameters"]["query"],
      paths["/v1/company/{uuid}"]["get"]["responses"][200]["content"]["application/json"]
    >
      uuid={uuid}
      query={useCompanyByUuidQuery}
      queryResult={companyQuery}
      notFoundProps={{
        title: t("masterdata:CompanyNotFound"),
        message: t("masterdata:CompanyDoesNotExist"),
        description: t("masterdata:CheckCompanyIdOrContactSupport"),
        backTo: "~/masterdata/company",
        backToLabel: t("masterdata:BackToCompanyOverview"),
      }}
    >
      <UpdateViewContent company={companyQuery.data} uuid={uuid} />
    </UuidExistenceWrapper>
  );
}

function UpdateViewContent({ company, uuid }: { company?: Company; uuid: string }) {
  const { t } = useTranslation(["masterdata", "common"]);
  const pageTitle = t("masterdata:EditCompany");
  const pageSubtitle = t("common:ReviewAndSaveChangesDescription");
  return (
    <CenteredLayout variant={CenteredLayoutVariant.Form} title={pageTitle} subTitle={pageSubtitle}>
      {/* Key by uuid so the form is freshly constructed (correct defaultValues, including `type`)
          for every company instead of relying on a post-mount reset to rehydrate it. */}
      <CompanyUpdateFormFields key={company?.uuid ?? uuid} company={company} uuid={uuid} />
    </CenteredLayout>
  );
}
